/**
 * Admin-app AuthContext (W0.8).
 *
 * Mirrors the customer-app AuthContext shape but targets staff auth:
 *   - storage namespaces under `ferro.admin.token` / `ferro.admin.staff`
 *     (handled by the admin-app's lib/api.ts session helpers)
 *   - login uses the existing throwing `loginStaff(apiUrl, body)` helper
 *     (kept stable for backward compat with this same Login page) and
 *     wraps it into the ApiResponse-shaped login() the rest of the app
 *     expects.
 *   - exposes `staff` (with `role`) on the context so PermissionGate /
 *     PlatformAdminRoute can stay thin.
 *
 * Differences from the customer-app version:
 *   - No `register` flow — staff are invited, not self-registered.
 *   - `login(username, password)` rather than `login(email, password)`.
 *   - Provides `staff.role` so admin-only UI (audit log, platform nav)
 *     can gate without dipping into localStorage.
 *   - 401 auto-logout via `setOnUnauthorized` — same plumbing as
 *     customer-app, hooked into the admin-app's distinct module-scoped
 *     callback in lib/api.ts.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  ApiError,
  clearSession,
  loginStaff,
  readSession,
  saveSession,
  setOnUnauthorized,
  validateStaffSession,
  type ApiResponse,
  type StaffLoginResponse,
  type StaffUser,
} from '../lib/api.js';

export interface AdminAuthContextValue {
  staff: StaffUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * BUG-6 — true while the on-mount `GET /api/auth/staff/me` token validation
   * is in flight. The shell renders regardless (isLoading is hydrate-only);
   * data-heavy surfaces (the dashboard's ~8 widgets) hold their fetches until
   * this is false, so a stale token can't trigger the 401 burst before the
   * session is cleared. False when there's nothing to validate (no token / no
   * apiUrl) or once validation resolves.
   */
  isValidatingSession: boolean;
  /**
   * Username + password login. Returns ApiResponse<StaffLoginResponse>
   * (never throws). On success, the session is saved automatically.
   */
  login(
    apiUrl: string,
    username: string,
    password: string,
  ): Promise<ApiResponse<StaffLoginResponse>>;
  logout(): void;
  refresh(): void;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export function AuthProvider({
  children,
  apiUrl,
}: {
  children: ReactNode;
  /**
   * API base URL (`config.deployment.apiUrl`). When provided, the persisted
   * token is validated against `GET /api/auth/staff/me` on mount (BUG-6).
   * Optional so unit harnesses that don't exercise the probe can omit it.
   */
  apiUrl?: string;
}): JSX.Element {
  const [staff, setStaff] = useState<StaffUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // True on the very first render when there's a token to validate, so the
  // dashboard holds its widgets from the first paint (no race where they fetch
  // before the validate effect flips this true).
  const [isValidatingSession, setIsValidatingSession] = useState<boolean>(
    () => Boolean(apiUrl) && Boolean(readSession().token),
  );

  // Mount: hydrate from localStorage. Synchronous — the shell renders
  // immediately (ProtectedRoute only blocks on isLoading, the hydrate flag).
  useEffect(() => {
    const session = readSession();
    if (session.token) {
      setToken(session.token);
      setStaff(session.staff);
    }
    setIsLoading(false);
  }, []);

  // BUG-6: validate the persisted token against the server on mount. This does
  // NOT block the shell — instead it drives `isValidatingSession`, which the
  // dashboard uses to hold its ~8 widget fetches until the token is confirmed.
  // That prevents the stale-token 401 burst (8 dead-token requests → first 401
  // self-logout → bounce). On 'invalid' we clear the session here (→ isAuthenticated
  // false → ProtectedRoute bounces to /login) in ONE request. A transient/unknown
  // result keeps the session (a Neon cold-start must not force a logout). With no
  // apiUrl (unit harnesses) we skip validation entirely.
  useEffect(() => {
    const session = readSession();
    if (!apiUrl || !session.token) {
      setIsValidatingSession(false);
      return;
    }
    let cancelled = false;
    setIsValidatingSession(true);
    void validateStaffSession(apiUrl)
      .then((result) => {
        if (cancelled) return;
        if (result === 'invalid') {
          clearSession();
          setStaff(null);
          setToken(null);
        }
        // 'valid' | 'unknown' → keep the session.
      })
      .finally(() => {
        if (!cancelled) setIsValidatingSession(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  const handleLogout = useCallback((): void => {
    clearSession();
    setStaff(null);
    setToken(null);
  }, []);

  // Register the global 401 handler. Cleared on unmount so tests don't
  // leak listeners across renders.
  useEffect(() => {
    setOnUnauthorized(() => {
      handleLogout();
    });
    return () => setOnUnauthorized(null);
  }, [handleLogout]);

  const login = useCallback(
    async (
      apiUrl: string,
      username: string,
      password: string,
    ): Promise<ApiResponse<StaffLoginResponse>> => {
      try {
        const data = await loginStaff(apiUrl, { username, password });
        saveSession(data.token, data.staff);
        setToken(data.token);
        setStaff(data.staff);
        return { data };
      } catch (err) {
        if (err instanceof ApiError) {
          return { error: err.message };
        }
        if (err instanceof Error) {
          return { error: err.message };
        }
        return { error: 'Unexpected error' };
      }
    },
    [],
  );

  const refresh = useCallback((): void => {
    const session = readSession();
    setToken(session.token);
    setStaff(session.staff);
  }, []);

  const value: AdminAuthContextValue = {
    staff,
    token,
    isAuthenticated: !!token,
    isLoading,
    isValidatingSession,
    login,
    logout: handleLogout,
    refresh,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
